import { SubAccountStates, SubTradeRequestTypes } from '@generated/graphql';
import type { PortfolioReport } from '@interfaces/portfolioReport';
import { CurrencyCodes } from '@onevesthq/ov-enums';
import type {
  DraftSubTradeRequest,
  PortfolioSecurity,
  PortfolioSubAccount,
} from '@ovComponents/4-module/widgets/portfolio/components/interfaces';
import { holdingTradeKey } from '@ovComponents/4-module/widgets/portfolio/components/utils';
import type { OptimizerAuditV2Record } from '../../types/audit.types';
import type { WeightDiffDetailRow } from '../../types/snapshot.types';
import { formatAuditAccountType } from '../presentation/accountTypeLabels';
import { isCashEventRebalance } from '../presentation/reportLabels';
import {
  aggregateMultiCurrencyCashBreakdown,
  type MultiCurrencyCashBreakdownEntry,
} from './aggregateMultiCurrencyCashBreakdown';
import {
  applyReportHoldingsToDiffRows,
  hasReportHoldingsValues,
} from './applyReportHoldingsToDiffRows';
import type {
  ReportDisplayMetadata,
  ReportSubAccountDisplayMetadata,
} from './buildReportDisplayMetadata';
import { buildWeightDiffRows } from './buildWeightDiffRows';
import { filterSnapshotSubAccounts } from './filterSnapshotSubAccounts';
import { resolvePortfolioInvestableCentsFromAudit } from './resolvePortfolioInvestableCents';
import { resolveProposedCashCentsFromAudit } from './resolveProposedCashCents';

const resolvePositiveWeightPct = (
  primaryPct: number,
  fallbackPct: number,
): number => (primaryPct > 0 ? primaryPct : fallbackPct);

const resolveSecurityExpectedPercentage = (
  row: WeightDiffDetailRow,
  auditProposedSummary: boolean,
  usesReportHoldings: boolean,
  reportExpectedPercentage?: number,
): number => {
  if (auditProposedSummary) {
    if (reportExpectedPercentage != null && reportExpectedPercentage > 0) {
      return reportExpectedPercentage;
    }

    return resolvePositiveWeightPct(row.modelTargetPct, row.targetPct);
  }

  if (usesReportHoldings) {
    if (reportExpectedPercentage != null) {
      return reportExpectedPercentage;
    }

    return resolvePositiveWeightPct(row.modelTargetPct, row.targetPct);
  }

  return resolvePositiveWeightPct(row.modelTargetPct, row.targetPct);
};

export type AuditPortfolioSnapshot = {
  diffRows: WeightDiffDetailRow[];
  securities: PortfolioSecurity[];
  subAccounts: PortfolioSubAccount[];
  draftTrades: Map<string, DraftSubTradeRequest>;
  portfolioReportForCash?: Pick<
    PortfolioReport,
    | 'cashCents'
    | 'expectedCashCents'
    | 'expectedCashPercentage'
    | 'transferRebalanceRequired'
    | 'liquidateRebalanceRequired'
  >;
  portfolioCurrency: CurrencyCodes;
  isMultiCurrencyPortfolio: boolean;
  isCashEventRebalance: boolean;
  usesReportHoldings: boolean;
  portfolioInvestableCents: number;
  proposedCashCents: number;
  multiCurrencyCashBreakdown: MultiCurrencyCashBreakdownEntry[];
};

const buildFallbackSubAccountId = (accountType: string) =>
  `audit-sub-account-${accountType}`;

const resolveSnapshotSubAccountMetadata = (
  reportSubAccount: ReportSubAccountDisplayMetadata | undefined,
  applyReportHoldings: boolean,
  result: {
    inputMarketValueCents?: number | null;
    inputCashCents?: number | null;
  },
): ReportSubAccountDisplayMetadata | undefined => {
  if (!reportSubAccount) {
    return undefined;
  }

  if (applyReportHoldings) {
    return reportSubAccount;
  }

  return {
    ...reportSubAccount,
    marketValueCents: result.inputMarketValueCents ?? 0,
    cashCents: result.inputCashCents ?? 0,
  };
};

const resolveSnapshotSubAccountMarketValueCents = (
  usesReportHoldings: boolean,
  reportMarketValueCents: number | undefined,
  inputMarketValueCents: number | null | undefined,
): number => {
  if (!usesReportHoldings) {
    return inputMarketValueCents ?? 0;
  }

  return reportMarketValueCents ?? inputMarketValueCents ?? 0;
};

const resolveCashWeightPct = (
  cashCents: number,
  totalMarketValueCents: number,
): number =>
  totalMarketValueCents > 0 ? cashCents / totalMarketValueCents : 0;

const resolvePortfolioReportForCash = ({
  subAccounts,
  applyReportHoldings,
  reportMetadata,
}: {
  subAccounts: PortfolioSubAccount[];
  applyReportHoldings: boolean;
  reportMetadata?: ReportDisplayMetadata;
}): AuditPortfolioSnapshot['portfolioReportForCash'] => {
  if (subAccounts.length === 0) {
    return undefined;
  }

  if (applyReportHoldings && reportMetadata?.portfolioCash) {
    return reportMetadata.portfolioCash;
  }

  const cashCents = subAccounts.reduce(
    (sum, subAccount) => sum + subAccount.cashCents,
    0,
  );
  const snapshotExpectedCashCents = subAccounts.reduce(
    (sum, subAccount) => sum + subAccount.expectedCashCents,
    0,
  );
  const totalMarketValueCents = subAccounts.reduce(
    (sum, subAccount) => sum + subAccount.marketValueCents,
    0,
  );
  const reportCash = reportMetadata?.portfolioCash;
  const expectedCashCents =
    reportCash?.expectedCashCents ?? snapshotExpectedCashCents;

  return {
    cashCents,
    expectedCashCents,
    expectedCashPercentage:
      reportCash != null
        ? reportCash.expectedCashPercentage
        : resolveCashWeightPct(expectedCashCents, totalMarketValueCents),
    transferRebalanceRequired: reportCash?.transferRebalanceRequired ?? false,
    liquidateRebalanceRequired: reportCash?.liquidateRebalanceRequired ?? false,
  };
};

const buildPortfolioSubAccount = (
  accountType: string,
  marketValueCents: number,
  subAccountId: string,
  portfolioCurrency: CurrencyCodes,
  metadata?: ReportSubAccountDisplayMetadata,
  inputCashCents?: number | null,
): PortfolioSubAccount => ({
  id: metadata?.id ?? subAccountId,
  name: metadata?.name ?? accountType,
  type: metadata?.accountType ?? accountType,
  taxRank: metadata?.taxRank ?? 0,
  holdings: [],
  inactive: metadata?.inactive ?? false,
  state:
    metadata?.inactive === true
      ? SubAccountStates.Inactive
      : SubAccountStates.Active,
  marketValueCents,
  expectedCashCents: metadata?.expectedCashCents ?? inputCashCents ?? 0,
  cashCents: metadata?.cashCents ?? inputCashCents ?? 0,
  pendingWithdrawCents: metadata?.pendingWithdrawCents ?? 0,
  pendingContributionCents: metadata?.pendingContributionCents ?? 0,
  cashOnHoldToTradeCents: metadata?.cashOnHoldToTradeCents ?? 0,
  currency: metadata?.currency ?? portfolioCurrency,
  accountBaseCurrency:
    metadata?.accountBaseCurrency ?? metadata?.currency ?? portfolioCurrency,
  originalValues: metadata?.originalValues,
});

export type AuditPortfolioSnapshotOptions = {
  includeInactiveSubAccounts?: boolean;
  /** When false, summary target/current stay on audit run-time values (completed runs). */
  applyReportHoldings?: boolean;
};

export const buildAuditPortfolioSnapshot = (
  audit: OptimizerAuditV2Record | undefined,
  tickerMap: Record<string, string> = {},
  reportMetadata?: ReportDisplayMetadata,
  options: AuditPortfolioSnapshotOptions = {},
): AuditPortfolioSnapshot => {
  const rawDiffRows = buildWeightDiffRows(audit, tickerMap);
  const applyReportHoldings = options.applyReportHoldings ?? false;
  const usesReportHoldings =
    applyReportHoldings &&
    hasReportHoldingsValues(
      reportMetadata?.holdingsValuesByProductId ?? new Map(),
    );
  const diffRows = (
    usesReportHoldings
      ? applyReportHoldingsToDiffRows(
          rawDiffRows,
          reportMetadata!.holdingsValuesByProductId,
          reportMetadata!.totalMarketValueCents,
        )
      : rawDiffRows
  ).map((row) => {
    const reportHolding = reportMetadata?.holdingsValuesByProductId.get(
      row.financialProductId,
    );

    const reportExpectedValueCents = usesReportHoldings
      ? reportHolding?.expectedValueCents != null
        ? reportHolding.expectedValueCents
        : row.targetCents
      : reportHolding?.expectedValueCents != null &&
          reportHolding.expectedValueCents > 0
        ? reportHolding.expectedValueCents
        : undefined;

    const reportExpectedPercentage =
      reportHolding?.expectedPercentage != null &&
      reportHolding.expectedPercentage > 0
        ? reportHolding.expectedPercentage
        : usesReportHoldings &&
            reportHolding?.expectedValueCents != null &&
            reportMetadata?.totalMarketValueCents &&
            reportMetadata.totalMarketValueCents > 0
          ? reportHolding.expectedValueCents /
            reportMetadata.totalMarketValueCents
          : undefined;

    return {
      ...row,
      reportExpectedValueCents,
      reportExpectedPercentage,
    };
  });
  const draftTrades = new Map<string, DraftSubTradeRequest>();
  const subAccountById = new Map<string, PortfolioSubAccount>();
  const cashEventRebalance = isCashEventRebalance(
    reportMetadata?.portfolioCash
      ? ({
          transferRebalanceRequired:
            reportMetadata.portfolioCash.transferRebalanceRequired,
          liquidateRebalanceRequired:
            reportMetadata.portfolioCash.liquidateRebalanceRequired,
        } as PortfolioReport)
      : undefined,
    audit,
  );
  const portfolioCurrency = reportMetadata?.baseCurrency ?? CurrencyCodes.CAD;
  const isMultiCurrencyPortfolio =
    (reportMetadata?.displayCurrencies.length ?? 1) > 1;

  for (const result of audit?.subAccountResults ?? []) {
    const accountType = formatAuditAccountType(result.accountType);
    if (!accountType) {
      continue;
    }

    const subAccountId =
      result.subAccountId ?? buildFallbackSubAccountId(accountType);
    if (subAccountById.has(subAccountId)) {
      continue;
    }

    subAccountById.set(
      subAccountId,
      buildPortfolioSubAccount(
        accountType,
        resolveSnapshotSubAccountMarketValueCents(
          usesReportHoldings,
          reportMetadata?.subAccountsById.get(subAccountId)?.marketValueCents,
          result.inputMarketValueCents,
        ),
        subAccountId,
        portfolioCurrency,
        resolveSnapshotSubAccountMetadata(
          reportMetadata?.subAccountsById.get(subAccountId),
          applyReportHoldings,
          result,
        ),
        result.inputCashCents,
      ),
    );
  }

  const subAccounts = [...subAccountById.values()];

  const securities = diffRows.map((row) => {
    const holdingMeta = reportMetadata?.holdingsByProductId.get(
      row.financialProductId,
    );

    const securitySubAccounts = subAccounts.map((subAccount) => {
      const subAccountRow = row.subAccountRows.find(
        (entry) => entry.subAccountId === subAccount.id,
      );
      const deltaCents = subAccountRow?.deltaCents ?? 0;
      const currentCents = subAccountRow?.currentCents ?? 0;
      const proposedEndCents = Math.round(currentCents + deltaCents);

      if (subAccountRow && deltaCents !== 0) {
        draftTrades.set(
          holdingTradeKey(row.financialProductId, subAccount.id),
          {
            financialProductId: row.financialProductId,
            subAccountId: subAccount.id,
            amountCents: deltaCents,
            ticker: row.label,
            accountName: subAccount.name,
            type:
              deltaCents > 0
                ? SubTradeRequestTypes.Buy
                : SubTradeRequestTypes.Sell,
            currency: portfolioCurrency,
            isTemp: true,
            isSellAll:
              deltaCents < 0 && currentCents >= 0 && proposedEndCents === 0,
          },
        );
      }

      const reportHolding = reportMetadata?.holdingsValuesByProductId.get(
        row.financialProductId,
      );
      const reportSubAccountHolding = reportHolding?.bySubAccount.get(
        subAccount.id,
      );
      const holdingCurrency =
        holdingMeta?.currency ?? subAccount.currency ?? portfolioCurrency;

      return {
        ...subAccount,
        holding:
          subAccountRow && (currentCents !== 0 || deltaCents !== 0)
            ? {
                valueCents: currentCents,
                currency: holdingCurrency,
                quantity: reportSubAccountHolding?.quantity ?? 0,
                financialProduct: {
                  id: row.financialProductId,
                  ticker: row.label,
                  originalCurrency:
                    holdingMeta?.originalCurrency ?? holdingCurrency,
                },
              }
            : undefined,
      };
    });

    const reportHolding = reportMetadata?.holdingsValuesByProductId.get(
      row.financialProductId,
    );

    const auditProposedSummary = !usesReportHoldings;
    const securityCurrency = holdingMeta?.currency ?? portfolioCurrency;

    return {
      id: row.financialProductId,
      ticker: row.label,
      translatedName: holdingMeta?.translatedName || row.label,
      expectedValueCents: auditProposedSummary
        ? row.targetCents
        : (row.reportExpectedValueCents ?? row.targetCents),
      expectedPercentage: resolveSecurityExpectedPercentage(
        row,
        auditProposedSummary,
        usesReportHoldings,
        reportHolding?.expectedPercentage,
      ),
      currentPriceCents: holdingMeta?.currentPriceCents ?? 0,
      taxRank: holdingMeta?.taxRank ?? null,
      financialProductTaxRank: holdingMeta?.financialProductTaxRank ?? 0,
      settlementDays: holdingMeta?.settlementDays,
      valueCents: usesReportHoldings ? row.currentCents : row.proposedCents,
      quantity: reportHolding?.quantity ?? 0,
      subAccounts: securitySubAccounts,
      currency: securityCurrency,
      originalCurrency:
        holdingMeta?.originalCurrency ??
        holdingMeta?.currency ??
        securityCurrency,
    } satisfies PortfolioSecurity;
  });

  const portfolioReportForCash = resolvePortfolioReportForCash({
    subAccounts,
    applyReportHoldings,
    reportMetadata,
  });

  const filtered = filterSnapshotSubAccounts(
    {
      diffRows,
      securities,
      subAccounts,
      draftTrades,
      portfolioReportForCash,
      portfolioCurrency,
      isMultiCurrencyPortfolio,
      isCashEventRebalance: cashEventRebalance,
      usesReportHoldings,
      portfolioInvestableCents: resolvePortfolioInvestableCentsFromAudit(audit),
      proposedCashCents: resolveProposedCashCentsFromAudit(audit),
      multiCurrencyCashBreakdown: [],
    },
    diffRows,
    draftTrades,
    options,
  );

  return {
    ...filtered,
    multiCurrencyCashBreakdown: isMultiCurrencyPortfolio
      ? aggregateMultiCurrencyCashBreakdown(filtered.subAccounts)
      : [],
  };
};
